import { useEffect, useRef, useState } from 'react';
import config from '../../constants/config.js';
import './../../styles/Dashboard.css';
import { useToast } from '../../context/ToastProvider.js';
import MigrationDetailsView from './MigrationDetailsView.js';

function MigrationsTable( { migrations, shops, loading, pagination, goToPreviousPage, goToNextPage, onUpdate, onRefreshMigration, onSetStatus } ) {
    const [ openMenuIndex, setOpenMenuIndex ] = useState( null );
    const [ disableActions , setDisableActions ] = useState( false );
    const [ selectedMigration , setSelectedMigration ] = useState( null );

    const menuRefs = useRef( [] );
    const addToast = useToast();

    function toggleMenu( index ) {
        setOpenMenuIndex( ( prevIndex ) => ( prevIndex === index ? null : index ) );
    }

    function formatTimeAgo( timestamp ) {
        const now = new Date();
        const lastModified = new Date(timestamp);

        if ( isNaN( lastModified.getTime() ) ) {
            return "NEVER";
        }

        const diffInSeconds = Math.floor((now - lastModified) / 1000);
    
        if (diffInSeconds < 60) {
            return `${diffInSeconds} sec(s) ago`;
        } else if (diffInSeconds < 3600) {
            return `${Math.floor(diffInSeconds / 60)} min(s) ago`;
        } else if (diffInSeconds < 86400) {
            return `${Math.floor(diffInSeconds / 3600)} hour(s) ago`;
        } else if (diffInSeconds < 432000) {
            return `${Math.floor(diffInSeconds / 86400)} day(s) ago`;
        } else {
            return lastModified.toLocaleDateString();
        }
    }

    function formatStatus( status ) {
        let formattedStatus = status;

        const migrationStatus = {
            "STARTED": {
                background_color: "#f7cfc5ff",
                text_color: "#8a2d16ff",
                display_value: "MIGRATION STARTED"
            },
            "GATHERING_PRODUCT_DATA": {
                background_color: "#D1C4E9", 
                text_color: "#6137dfff",
                display_value: "GATHERING PRODUCT DATA"
            },
            "SORTING_PRODUCT_DATA": {
                background_color: "#F3E5F5",
                text_color: "#370f67ff",
                display_value: "ORGANIZING PRODUCT DATA"
            },
            "DELETING_PRODUCTS": {
                background_color: "#FFEBEE",
                text_color: "#d55785ff",
                display_value: "REMOVING OLD PRODUCTS"
            },
            "CREATING_PRODUCTS": {
                background_color: "#E3F2FD", 
                text_color: "#0D47A1",
                display_value: "CREATING NEW PRODUCTS"
            },
            "UPDATING_PRODUCT_METADATA": {
                background_color: "#F3E5F5",
                text_color: "#4A148C",
                display_value: "UPDATING PRODUCT DETAILS"
            },
            "FINISHED": {
                background_color: "#E8F5E9",
                text_color: "#2E7D32",
                display_value: "MIGRATION COMPLETE"
            },
            "ABORTED": {
                background_color: "#FFEBEE",
                text_color: "#C62828",
                display_value: "MIGRATION ABORTED"
            }
        };

        const badgeInfo = migrationStatus[ status ];
        formattedStatus = (
            <div className="store-badges">
                <div
                    style={{ 
                        backgroundColor: badgeInfo?.background_color || '#ffffff',
                        color: badgeInfo?.text_color || '#000000' 
                    }} 
                    className="store-badge"
                >
                    { badgeInfo?.display_value || "" }
                </div>
            </div>
        );


        return formattedStatus;   
    }

    function formatShop( shop ) {
        let formattedShop = shop;

        for ( let i = 0; i < shops.length; i++ ) {
            const store = shops[i];
            const shopDomain = store.shop;

            if ( shopDomain == shop ) {
                formattedShop = (
                    <div className="store-badges">
                        <div
                            style={{ 
                                backgroundColor: store?.badge_background_color || '#ffffff',
                                color: store?.badge_text_color || '#000000' 
                            }} 
                            className="store-badge"
                        >
                            { store?.nickname || "" }
                        </div>
                    </div>
                );

                break;
            }

        }

        return formattedShop;
    }

    function checkAgeOlderThan( date , maxAgeAllowedHours = 6 ) {
        let older = false;

        const inputDate = new Date( date );
        const now = new Date();
        const diffMs = now - inputDate;
        const diffHours = diffMs / ( 1000 * 60 * 60 );

        older = ( diffHours > maxAgeAllowedHours );

        return older;
    }

    useEffect(() => {
        function handleClickOutside(event) {
            if ( openMenuIndex !== null && menuRefs.current[openMenuIndex] && 
                !menuRefs.current[ openMenuIndex ].contains(event.target)) {
                setOpenMenuIndex(null);
            }
        }

        if ( openMenuIndex !== null ) {
            document.addEventListener( "mousedown", handleClickOutside );
        }

        return () => {
            document.removeEventListener( "mousedown", handleClickOutside );
        };
    }, [ openMenuIndex ] );

    if ( loading ) {
        return(
            <div className="product-table-skeleton"></div>
        )
    }

    return(
        <div>
            <div className="dashboard-table">
                <table id="recent-products">
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>FROM STORE</th>
                            <th>TO STORE</th>
                            <th>TOTAL PRODUCTS</th>
                            <th>CREATED BY</th>
                            <th>CREATED AT</th>
                            <th>LAST UPDATED</th>
                            <th>STATUS</th>
                            <th>{/* ACTIONS */}</th>
                        </tr>
                    </thead>
                    <tbody>
                        { 
                            migrations?.length > 0 ? 
                                migrations?.map( ( row , index ) => {
                                    return(
                                        <tr className="product-row" key={ index }>
                                            <td>
                                                <div style={{ minWidth: 30 }} className="id normal">
                                                    { formatShop( row?.id || "" ) }
                                                </div>
                                            </td>
                                            <td>
                                                <div className="from-store normal">
                                                    { formatShop( row?.from_store || "" ) }
                                                </div>
                                            </td>
                                            <td>
                                                <div className="to-store normal">
                                                    { formatShop( row?.to_store || "" ) }
                                                </div>
                                            </td>
                                             <td>
                                                <div className="product-count normal">
                                                    { row?.product_count || "" }
                                                </div>
                                            </td>
                                             <td>
                                                <div className="created-by normal">
                                                    { row?.created_by || "" }
                                                </div>
                                            </td>
                                            <td>
                                                <div className="created-at normal">
                                                    { formatTimeAgo( row?.created_at ) }
                                                </div>
                                            </td>
                                            <td>
                                                <div className="updated-at normal">
                                                    { formatTimeAgo( row?.updated_at ) }
                                                </div>
                                            </td>
                                            <td>
                                                <div className="status normal">
                                                    { formatStatus( row?.status ) }
                                                </div>
                                            </td>
                                            <td>
                                                <div className="actions" ref={(el) => menuRefs.current[index] = el}>
                                                    <button className="more-options" onClick={() => toggleMenu( index )}>
                                                        ...
                                                    </button>

                                                    { openMenuIndex === index && (
                                                        <ul className="options-menu">
                                                            {/* 
                                                                Modal view that shows ALL fields and a status bar ( percentage ) ( errors , products JSON )
                                                                When view is open and the process is not finished, ping server every X seconds to see the status of the migration
                                                            */}
                                                            <li className="option-menu-li">
                                                                <button disabled={disableActions} className="option-action" onClick={ () => {
                                                                    setSelectedMigration( row );
                                                                    toggleMenu( index );
                                                                } }>
                                                                    View Details
                                                                </button>
                                                            </li>

                                                            <li className="option-menu-li">
                                                                <button disabled={disableActions || row?.status === "FINISHED" || row?.status === "ABORTED"} className="option-action" onClick={ () => {
                                                                    addToast( `Refreshing Migration ${row?.id}`, false , 1000 );
                                                                    onRefreshMigration( row?.id );
                                                                    toggleMenu( index );
                                                                } }>
                                                                    Refresh
                                                                </button>
                                                            </li>
                                                            
                                                            <li className="option-menu-li">
                                                                <button disabled={
                                                                    disableActions || 
                                                                    row?.status == "FINISHED" || 
                                                                    row?.status == "ABORTED" || 
                                                                    !checkAgeOlderThan( row?.created_at )
                                                                } className="option-action" onClick={ () => {
                                                                    addToast( `Setting Migration Status for ${row?.id}`, false , 1000 );
                                                                    onSetStatus( row?.id , "ABORTED" );
                                                                    toggleMenu( index );
                                                                } }>
                                                                    Set status to "ABORTED"
                                                                </button>
                                                            </li>
                                                            
                                                            
                                                        </ul>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    )
                                })
                            :
                            <tr>
                                <td colSpan="9">No Migrations Found</td>
                            </tr>
                        }
                    </tbody>
                </table>

                { pagination != null && pagination?.totalPages > 1 &&
                    <div className="pagination">
                        <button onClick={goToPreviousPage} disabled={pagination.currentPage === 1}>
                            Previous
                        </button>
                        <span>Page {pagination.currentPage} of {pagination.totalPages}</span>
                        <button onClick={goToNextPage} disabled={pagination.currentPage === pagination.totalPages}>
                            Next
                        </button>
                    </div>
                }
                
                { selectedMigration != null && 
                    <MigrationDetailsView 
                        migration={ selectedMigration }
                        shops={ shops }
                        onClose={ () => { setSelectedMigration( null ) } }
                    />
                }

                { }
            </div>
        </div>
    )
}

export default MigrationsTable;